import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import VehicleGallery from './components/VehicleGallery';
import VehicleSpecs from './components/VehicleSpecs';
import OwnerInfo from './components/OwnerInfo';
import VehicleSwapProposal from './components/VehicleSwapProposal';
import SimilarVehicles from './components/SimilarVehicles';
import { vehicles } from '../../mocks/vehicles';

export default function VehicleDetailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const vehicleId = searchParams.get('id');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showProposal, setShowProposal] = useState(false);

  const vehicle = vehicles.find(v => v.id === Number(vehicleId));

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
            <i className="ri-car-line text-4xl text-gray-400"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Vehicle Not Found</h2>
          <p className="text-gray-600 mb-6">The vehicle you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/browse')}
            className="px-6 py-3 bg-teal-500 text-white font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Back to Browse
          </button>
        </div>
      </div>
    );
  }

  const similarVehicles = vehicles
    .filter(v => v.make === vehicle.make && v.id !== vehicle.id)
    .slice(0, 3);

  const handleCompare = () => {
    window.location.href = `/compare-vehicles?ids=${vehicle.id}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isScrolled={isScrolled} />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
            <button
              onClick={() => navigate('/')}
              className="hover:text-teal-600 transition-colors cursor-pointer"
            >
              Home
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <button
              onClick={() => navigate('/browse')}
              className="hover:text-teal-600 transition-colors cursor-pointer"
            >
              Browse Vehicles
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900 font-medium">{vehicle.year} {vehicle.make} {vehicle.model}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Images & Details */}
            <div className="lg:col-span-2">
              <VehicleGallery images={vehicle.images} title={`${vehicle.year} ${vehicle.make} ${vehicle.model}`} />

              {/* Vehicle Overview with Condition Badges */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
                <div className="flex items-start justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">Vehicle Overview</h2>
                  <div className="flex flex-wrap gap-2 justify-end">
                    {/* Condition Badge */}
                    {vehicle.condition === 'Excellent' && (
                      <span className="px-4 py-2 bg-emerald-100 text-emerald-800 text-sm font-semibold rounded-lg border border-emerald-300 flex items-center gap-2 whitespace-nowrap">
                        <i className="ri-star-fill"></i>
                        Excellent Condition
                      </span>
                    )}
                    {vehicle.condition === 'Very Good' && (
                      <span className="px-4 py-2 bg-teal-100 text-teal-800 text-sm font-semibold rounded-lg border border-teal-300 flex items-center gap-2 whitespace-nowrap">
                        <i className="ri-star-line"></i>
                        Very Good
                      </span>
                    )}
                    {vehicle.condition === 'Good' && (
                      <span className="px-4 py-2 bg-blue-100 text-blue-800 text-sm font-semibold rounded-lg border border-blue-300 flex items-center gap-2 whitespace-nowrap">
                        <i className="ri-checkbox-circle-line"></i>
                        Good Condition
                      </span>
                    )}
                    {vehicle.condition === 'Fair' && (
                      <span className="px-4 py-2 bg-amber-100 text-amber-800 text-sm font-semibold rounded-lg border border-amber-300 flex items-center gap-2 whitespace-nowrap">
                        <i className="ri-information-line"></i>
                        Fair Condition
                      </span>
                    )}
                    
                    {/* Title Status Badge */}
                    {vehicle.titleStatus === 'Clean Title' && (
                      <span className="px-4 py-2 bg-green-100 text-green-800 text-sm font-semibold rounded-lg border border-green-300 flex items-center gap-2 whitespace-nowrap">
                        <i className="ri-shield-check-fill"></i>
                        Clean Title
                      </span>
                    )}
                    
                    {/* No Accidents Badge */}
                    {vehicle.accidents === 0 && (
                      <span className="px-4 py-2 bg-blue-100 text-blue-800 text-sm font-semibold rounded-lg border border-blue-300 flex items-center gap-2 whitespace-nowrap">
                        <i className="ri-shield-star-line"></i>
                        No Accidents
                      </span>
                    )}
                    
                    {/* Single Owner Badge */}
                    {vehicle.owners === 1 && (
                      <span className="px-4 py-2 bg-purple-100 text-purple-800 text-sm font-semibold rounded-lg border border-purple-300 flex items-center gap-2 whitespace-nowrap">
                        <i className="ri-user-star-line"></i>
                        Single Owner
                      </span>
                    )}
                  </div>
                </div>
                
                <p className="text-gray-700 leading-relaxed mb-6">
                  {vehicle.description}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  This {vehicle.year} {vehicle.make} {vehicle.model} is in {vehicle.condition.toLowerCase()} condition 
                  with {vehicle.mileage.toLocaleString()} miles on the odometer. The vehicle has been well-maintained 
                  and comes with a clean title. {vehicle.accidents === 0 ? 'No accidents reported.' : `${vehicle.accidents} accident(s) reported.`}
                </p>
              </div>

              {/* Detailed Specifications */}
              <VehicleSpecs vehicle={vehicle} />

              {/* Features & Options */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Features & Options</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {vehicle.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-6 h-6 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                        <i className="ri-check-line text-teal-600 text-sm"></i>
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vehicle History */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Vehicle History</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 pb-4 border-b border-gray-200">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                      <i className="ri-file-list-3-line text-xl text-teal-600"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">Title Status</h3>
                      <p className="text-gray-700">{vehicle.titleStatus}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 pb-4 border-b border-gray-200">
                    <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                      <i className="ri-alert-line text-xl text-amber-600"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">Accident History</h3>
                      <p className="text-gray-700">
                        {vehicle.accidents === 0 ? 'No accidents reported' : `${vehicle.accidents} accident(s) reported`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full flex-shrink-0">
                      <i className="ri-user-line text-xl text-blue-600"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">Ownership</h3>
                      <p className="text-gray-700">{vehicle.owners} previous owner(s)</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Swap Preferences */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Swap Preferences</h2>
                <p className="text-gray-700 mb-4">
                  The owner is interested in swapping for vehicles with the following criteria:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <i className="ri-car-line text-teal-600"></i>
                    <span className="text-gray-700">Similar or higher value vehicles</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-calendar-line text-teal-600"></i>
                    <span className="text-gray-700">Year 2018 or newer preferred</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-speed-line text-teal-600"></i>
                    <span className="text-gray-700">Under 80,000 miles</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-shield-check-line text-teal-600"></i>
                    <span className="text-gray-700">Clean title required</span>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Location</h2>
                <div className="w-full h-80 bg-gray-100 rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2795.3032145384!2d-122.67648!3d45.52345!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDXCsDMxJzI0LjQiTiAxMjLCsDQwJzM1LjMiVw!5e0!3m2!1sen!2sus!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
                <div className="flex items-center gap-2 mt-4 text-gray-700">
                  <i className="ri-map-pin-line text-xl text-teal-600"></i>
                  <span className="font-medium">{vehicle.location}</span>
                </div>
              </div>
            </div>

            {/* Right Column - Actions & Owner Info */}
            <div className="lg:col-span-1">
              {/* Sticky Action Card */}
              <div className="sticky top-24">
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        {vehicle.year} {vehicle.make}
                      </h1>
                      <p className="text-xl text-gray-700 font-semibold mb-3">{vehicle.model} {vehicle.trim}</p>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className="px-3 py-1 bg-teal-50 text-teal-700 text-sm font-medium rounded-full whitespace-nowrap">
                          {vehicle.condition}
                        </span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full whitespace-nowrap">
                          {vehicle.transmission}
                        </span>
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full whitespace-nowrap">
                          {vehicle.drivetrain}
                        </span>
                      </div>
                      <div className="text-3xl font-bold text-teal-600">
                        ${vehicle.estimatedValue.toLocaleString()}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">Estimated Value</p>
                    </div>
                    <button
                      onClick={() => setIsFavorite(!isFavorite)}
                      className="w-12 h-12 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors flex-shrink-0 cursor-pointer"
                    >
                      <i className={`${isFavorite ? 'ri-heart-fill text-red-500' : 'ri-heart-line'} text-2xl`}></i>
                    </button>
                  </div>

                  <div className="space-y-3 mb-6">
                    <button
                      onClick={() => setShowProposal(true)}
                      className="w-full px-6 py-3.5 bg-teal-500 text-white font-semibold rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-swap-line mr-2"></i>
                      Propose Vehicle Swap
                    </button>
                    <button
                      onClick={handleCompare}
                      className="w-full px-6 py-3 bg-white text-teal-600 font-semibold rounded-lg border-2 border-teal-500 hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-scales-3-line mr-2"></i>
                      Add to Compare
                    </button>
                    <button
                      onClick={() => navigate('/messages')}
                      className="w-full px-6 py-3.5 bg-white text-teal-600 font-semibold rounded-lg border-2 border-teal-500 hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-message-3-line mr-2"></i>
                      Contact Owner
                    </button>
                  </div>

                  <div className="flex items-center justify-around pt-4 border-t border-gray-200">
                    <button className="flex flex-col items-center gap-1 text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                      <i className="ri-share-line text-xl"></i>
                      <span className="text-xs">Share</span>
                    </button>
                    <button className="flex flex-col items-center gap-1 text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                      <i className="ri-flag-line text-xl"></i>
                      <span className="text-xs">Report</span>
                    </button>
                    <button className="flex flex-col items-center gap-1 text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                      <i className="ri-printer-line text-xl"></i>
                      <span className="text-xs">Print</span>
                    </button>
                  </div>
                </div>

                <OwnerInfo owner={vehicle.owner} postedDate={vehicle.postedDate} />

                {/* Safety Tips */}
                <div className="bg-amber-50 rounded-xl border border-amber-200 p-6 mt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                      <i className="ri-shield-check-line text-xl text-amber-600"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Vehicle Swap Safety</h3>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Verify VIN and title status</li>
                        <li>• Get a pre-swap inspection</li>
                        <li>• Meet at safe public locations</li>
                        <li>• Check vehicle history report</li>
                        <li>• Test drive before finalizing</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Similar Vehicles */}
          {similarVehicles.length > 0 && (
            <SimilarVehicles vehicles={similarVehicles} />
          )}
        </div>
      </div>

      <Footer />

      {/* Swap Proposal Modal */}
      {showProposal && (
        <VehicleSwapProposal
          vehicleTitle={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          onClose={() => setShowProposal(false)}
        />
      )}
    </div>
  );
}
